// usePlanUpgrade Hook
// Custom hook for handling plan upgrades and cancellations

import { useState, useCallback } from 'react';
import { billingAPI, type UpgradePlanResponse, type CancelSubscriptionResponse } from '../api/billing';

import { logger } from '../utils/logger';

interface UsePlanUpgradeReturn {
  upgrading: boolean;
  cancelling: boolean;
  error: string | null;
  upgradePlan: (
    organizationId: string,
    planType: 'pro' | 'team' | 'enterprise' | 'starter' | 'agency',
    billingInterval?: 'monthly' | 'yearly',
    currency?: 'usd' | 'eur' | 'czk'
  ) => Promise<UpgradePlanResponse | null>;
  cancelSubscription: (
    organizationId: string,
    cancelAtPeriodEnd?: boolean,
    reason?: string
  ) => Promise<CancelSubscriptionResponse | null>;
  clearError: () => void;
}

/**
 * Custom hook for plan upgrade and cancellation operations
 * @returns Functions to upgrade/cancel plans with loading states
 */
export function usePlanUpgrade(): UsePlanUpgradeReturn {
  const [upgrading, setUpgrading] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Upgrade plan
  const upgradePlan = useCallback(
    async (
      organizationId: string,
      planType: 'pro' | 'team' | 'enterprise' | 'starter' | 'agency',
      billingInterval: 'monthly' | 'yearly' = 'monthly',
      currency: 'usd' | 'eur' | 'czk' = 'usd'
    ): Promise<UpgradePlanResponse | null> => {
      try {
        setUpgrading(true);
        setError(null);

        logger.log('🔵 Upgrading plan to:', planType, 'interval:', billingInterval, 'currency:', currency);
        const result = await billingAPI.upgradePlan(organizationId, planType, billingInterval, currency);

        logger.log('✅ Upgrade response:', result);

        // Backend always returns checkout_url for paid plans
        if (result.checkout_url) {
          logger.log('🔗 Redirecting to Stripe Checkout:', result.checkout_url);
          // Keep upgrading state true during redirect
          window.location.href = result.checkout_url;
          return result;
        }

        // For free plan or immediate upgrades (no payment required)
        logger.log('✅ Plan upgraded immediately (no payment required)');
        return result;
      } catch (err: any) {
        logger.error('❌ Error upgrading plan:', err);

        // Handle specific error types
        let errorMessage = 'Failed to upgrade plan';
        let errorData: any = null;

        // Get error detail from authenticatedFetch (attached as err.detail)
        const detail = err.detail || null;

        // Handle structured error detail
        if (detail && typeof detail === 'object') {
          // Currency mismatch error
          if (detail.error === 'currency_mismatch') {
            errorMessage = detail.message || 'Cannot change to a plan with a different currency. Your subscription is locked to the original currency.';
            errorData = {
              error: 'currency_mismatch',
              message: errorMessage,
              original_error: detail.original_error
            };
          }
          // Currency locked error
          else if (detail.error === 'currency_locked') {
            errorMessage = `Your account is locked to ${detail.locked_currency}. Please select ${detail.locked_currency} as your currency.`;
            errorData = {
              error: 'currency_locked',
              locked_currency: detail.locked_currency
            };
          }
          // Generic structured error with message
          else if (detail.message) {
            errorMessage = detail.message;
          }
        }
        // Fallback to error message
        else if (err instanceof Error && err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setUpgrading(false);

        // Return error data if available (for currency lock/mismatch handling)
        if (errorData) {
          return errorData as any;
        }

        return null;
      }
      // Note: Don't set upgrading to false if redirecting to Stripe
    },
    []
  );

  // Cancel subscription
  const cancelSubscription = useCallback(
    async (
      organizationId: string,
      cancelAtPeriodEnd: boolean = true,
      reason?: string
    ): Promise<CancelSubscriptionResponse | null> => {
      try {
        setCancelling(true);
        setError(null);
        
        logger.log('🔵 Cancelling subscription, at period end:', cancelAtPeriodEnd);
        const result = await billingAPI.cancelSubscription(organizationId, cancelAtPeriodEnd, reason);
        
        logger.log('✅ Subscription cancelled successfully:', result);
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to cancel subscription';
        logger.error('❌ Error cancelling subscription:', errorMessage);
        setError(errorMessage);
        return null;
      } finally {
        setCancelling(false);
      }
    },
    []
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    upgrading,
    cancelling,
    error,
    upgradePlan,
    cancelSubscription,
    clearError,
  };
}

